import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import Navbar from "../../components/Navbar/Navbar";
import CustomHeader from "../../components/CustomHeader/CustomHeader";
import "../GChat/GChat.css";

const SourceThreadsRenderer = ({ value }) => {
  if (!value || value.length === 0) return "—";
  return (
    <div className="gchat-source-threads">
      {value.map((st, i) => (
        <a
          key={i}
          href={st.thread_link}
          target="_blank"
          rel="noopener noreferrer"
          className="gchat-space-chip-link"
          title={`Open thread in ${st.space_display_name}`}
        >
          {st.space_display_name}
        </a>
      ))}
    </div>
  );
};

const GChatConversations = () => {
  const today = new Date().toISOString().split("T")[0];

  const [spaces, setSpaces] = useState([]);
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("unified");

  const [filterSearch, setFilterSearch] = useState("");
  const [filterSpace, setFilterSpace] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [selectedThreadIds, setSelectedThreadIds] = useState([]);
  const [messagesDownloading, setMessagesDownloading] = useState(false);

  const fetchSpaces = useCallback(async () => {
    try {
      const res = await axios.get("/gchat/spaces");
      setSpaces(res.data);
    } catch (err) {
      console.error("Failed to fetch spaces:", err);
    }
  }, []);

  const fetchThreads = useCallback(
    async (params = {}) => {
      setThreadsLoading(true);
      try {
        const endpoint =
          viewMode === "unified" ? "/gchat/unified-threads" : "/gchat/threads";
        const res = await axios.get(endpoint, { params });
        const parsed = res.data.map((t) => ({
          ...t,
          conversation_start: t.conversation_start
            ? new Date(t.conversation_start)
            : null,
        }));
        setThreads(parsed);
      } catch (err) {
        console.error("Failed to fetch threads:", err);
      } finally {
        setThreadsLoading(false);
      }
    },
    [viewMode]
  );

  const handleSearchThreads = () => {
    const params = {};
    if (filterSearch) params.search = filterSearch;
    if (filterSpace) params.space = filterSpace;
    if (filterStartDate) params.start_date = filterStartDate;
    if (filterEndDate) params.end_date = filterEndDate;
    fetchThreads(params);
  };

  const handleClearFilters = () => {
    setFilterSearch("");
    setFilterSpace("");
    setFilterStartDate("");
    setFilterEndDate("");
    fetchThreads();
  };

  const handleDownloadCsv = async () => {
    try {
      const params = {};
      if (filterSearch) params.search = filterSearch;
      if (filterSpace) params.space = filterSpace;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;
      const res = await axios.get("/gchat/threads/csv", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "chat_root_causes.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download CSV.");
    }
  };

  const handleDownloadMessagesCsv = async () => {
    setMessagesDownloading(true);
    try {
      const params = {};
      if (filterSearch) params.search = filterSearch;
      if (filterSpace) params.space = filterSpace;
      if (filterStartDate) params.start_date = filterStartDate;
      if (filterEndDate) params.end_date = filterEndDate;
      const res = await axios.get("/gchat/threads/messages/csv", {
        params,
        responseType: "blob",
        timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "chat_messages.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(
        "Failed to download messages CSV. This operation requires Google Chat API access and may take a while for large datasets."
      );
    } finally {
      setMessagesDownloading(false);
    }
  };

  const handleDeleteThread = async (threadId) => {
    if (!window.confirm("Delete this conversation analysis?")) return;
    try {
      await axios.delete(`/gchat/threads/${threadId}`);
      setThreads((prev) => prev.filter((t) => t.id !== threadId));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete thread.");
    }
  };

  const onSelectionChanged = useCallback((event) => {
    const selected = event.api.getSelectedRows().map((r) => r.id);
    setSelectedThreadIds(selected);
  }, []);

  const handleBulkDelete = async () => {
    if (selectedThreadIds.length === 0) return;
    if (
      !window.confirm(
        `Delete ${selectedThreadIds.length} selected conversation(s)?`
      )
    )
      return;
    try {
      await axios.post("/gchat/threads/bulk-delete", {
        ids: selectedThreadIds,
      });
      setThreads((prev) =>
        prev.filter((t) => !selectedThreadIds.includes(t.id))
      );
      setSelectedThreadIds([]);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete threads.");
    }
  };

  useEffect(() => {
    fetchSpaces();
  }, [fetchSpaces]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const unifiedColumnDefs = [
    {
      headerName: "Source Threads",
      field: "source_threads",
      sortable: false,
      filter: false,
      resizable: true,
      width: 220,
      wrapText: true,
      autoHeight: true,
      headerComponentFramework: CustomHeader,
      cellRenderer: SourceThreadsRenderer,
    },
    {
      headerName: "Threads",
      field: "thread_count",
      sortable: true,
      filter: false,
      resizable: true,
      width: 120,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        const count = params.value || 1;
        const isUnified = params.data?.type === "unified";
        const sourceThreads = params.data?.source_threads || [];
        const firstLink = sourceThreads[0]?.thread_link;
        const badge = (
          <span
            className={
              isUnified
                ? "gchat-thread-count-badge unified"
                : "gchat-thread-count-badge"
            }
          >
            {count} {count === 1 ? "thread" : "threads"}
          </span>
        );
        if (firstLink) {
          return (
            <a
              href={firstLink}
              target="_blank"
              rel="noopener noreferrer"
              className="gchat-thread-link"
            >
              {badge}
              <span className="gchat-open-icon">&#8599;</span>
            </a>
          );
        }
        return badge;
      },
    },
    {
      headerName: "Issue Description",
      field: "issue_description",
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return <div className="gchat-wrap-cell">{params.value}</div>;
      },
    },
    {
      headerName: "Root Cause Summary",
      field: "root_cause_summary",
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 250,
      wrapText: true,
      autoHeight: true,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return (
          <div className="gchat-truncated-cell" title={params.value}>
            {params.value}
          </div>
        );
      },
    },
    {
      headerName: "Affected Customers",
      field: "affected_customers",
      sortable: true,
      filter: true,
      resizable: true,
      width: 200,
      wrapText: true,
      autoHeight: true,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "N/A";
        return <div className="gchat-wrap-cell">{params.value}</div>;
      },
    },
    {
      headerName: "RCA Document",
      field: "rca_doc_link",
      sortable: false,
      filter: false,
      resizable: true,
      width: 130,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            className="gchat-link"
          >
            Open RCA
          </a>
        );
      },
    },
    {
      headerName: "Conversation Start",
      field: "conversation_start",
      sortable: true,
      filter: true,
      resizable: true,
      width: 190,
      headerComponentFramework: CustomHeader,
      valueFormatter: (params) => {
        if (!params.value) return "—";
        return params.value.toLocaleString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
  ];

  const allThreadsColumnDefs = [
    {
      headerName: "Space Name",
      field: "space_display_name",
      sortable: true,
      filter: true,
      resizable: true,
      width: 160,
      headerComponentFramework: CustomHeader,
      checkboxSelection: true,
      headerCheckboxSelection: true,
    },
    {
      headerName: "Chat Thread",
      field: "thread_link",
      sortable: false,
      filter: false,
      resizable: true,
      width: 130,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            className="gchat-link"
          >
            Open Thread
          </a>
        );
      },
    },
    {
      headerName: "Issue Description",
      field: "issue_description",
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return <div className="gchat-wrap-cell">{params.value}</div>;
      },
    },
    {
      headerName: "Root Cause Summary",
      field: "root_cause_summary",
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 250,
      wrapText: true,
      autoHeight: true,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return (
          <div className="gchat-truncated-cell" title={params.value}>
            {params.value}
          </div>
        );
      },
    },
    {
      headerName: "Affected Customers",
      field: "affected_customers",
      sortable: true,
      filter: true,
      resizable: true,
      width: 200,
      wrapText: true,
      autoHeight: true,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "N/A";
        return <div className="gchat-wrap-cell">{params.value}</div>;
      },
    },
    {
      headerName: "RCA Document",
      field: "rca_doc_link",
      sortable: false,
      filter: false,
      resizable: true,
      width: 130,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "—";
        return (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            className="gchat-link"
          >
            Open RCA
          </a>
        );
      },
    },
    {
      headerName: "Google Meet Link",
      field: "meet_link",
      sortable: false,
      filter: false,
      resizable: true,
      width: 150,
      headerComponentFramework: CustomHeader,
      cellRenderer: (params) => {
        if (!params.value) return "N/A";
        return (
          <a
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            className="gchat-link"
          >
            Join Meet
          </a>
        );
      },
    },
    {
      headerName: "Meet Posted By",
      field: "meet_poster_email",
      sortable: true,
      filter: true,
      resizable: true,
      minWidth: 220,
      headerComponentFramework: CustomHeader,
      cellStyle: { whiteSpace: "normal", wordBreak: "break-all" },
      wrapText: true,
      autoHeight: true,
      valueFormatter: (params) => params.value || "N/A",
    },
    {
      headerName: "Conversation Start",
      field: "conversation_start",
      sortable: true,
      filter: true,
      resizable: true,
      width: 190,
      headerComponentFramework: CustomHeader,
      valueFormatter: (params) => {
        if (!params.value) return "—";
        return params.value.toLocaleString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    {
      headerName: "",
      field: "id",
      sortable: false,
      filter: false,
      resizable: false,
      width: 80,
      cellRenderer: (params) => (
        <button
          className="gchat-delete-btn"
          onClick={() => handleDeleteThread(params.value)}
          title="Delete this conversation"
        >
          Delete
        </button>
      ),
    },
  ];

  const columnDefs =
    viewMode === "unified" ? unifiedColumnDefs : allThreadsColumnDefs;

  const defaultColDef = {
    sortable: true,
    resizable: true,
  };

  return (
    <div>
      <Navbar />
      <div className="gchat-container">
        <div className="gchat-header">
          <h1>Chat Conversations</h1>
          <p>Browse and search analysed Google Chat conversations.</p>
        </div>

        <div className="gchat-card">
          <div className="gchat-card-title-row">
            <h2>Conversation Analysis</h2>
            <div className="gchat-view-toggle">
              <button
                className={`gchat-toggle-btn ${viewMode === "unified" ? "active" : ""}`}
                onClick={() => setViewMode("unified")}
              >
                Unified View
              </button>
              <button
                className={`gchat-toggle-btn ${viewMode === "all" ? "active" : ""}`}
                onClick={() => setViewMode("all")}
              >
                All Threads
              </button>
            </div>
          </div>

          <div className="gchat-filter-bar">
            <input
              type="text"
              className="gchat-filter-input"
              placeholder="Search issues or root cause..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearchThreads()}
            />
            <select
              className="gchat-filter-select"
              value={filterSpace}
              onChange={(e) => setFilterSpace(e.target.value)}
            >
              <option value="">All Spaces</option>
              {spaces.map((s) => (
                <option key={s.id} value={s.display_name}>
                  {s.display_name}
                </option>
              ))}
            </select>
            <div className="gchat-filter-dates">
              <input
                type="date"
                value={filterStartDate}
                max={today}
                onChange={(e) => setFilterStartDate(e.target.value)}
                title="Conversation start from"
              />
              <span className="gchat-filter-date-sep">to</span>
              <input
                type="date"
                value={filterEndDate}
                max={today}
                onChange={(e) => setFilterEndDate(e.target.value)}
                title="Conversation start to"
              />
            </div>
            <button className="gchat-filter-btn" onClick={handleSearchThreads}>
              Search
            </button>
            <button
              className="gchat-filter-clear-btn"
              onClick={handleClearFilters}
            >
              Clear
            </button>
            <button
              className="gchat-filter-btn gchat-csv-btn"
              onClick={handleDownloadCsv}
              title="Download current results as CSV"
            >
              Download CSV
            </button>
            <button
              className="gchat-filter-btn gchat-csv-btn"
              onClick={handleDownloadMessagesCsv}
              disabled={messagesDownloading}
              title="Download raw chat messages for all threads as CSV (fetches from Google Chat API)"
            >
              {messagesDownloading ? "Downloading..." : "Download Messages CSV"}
            </button>
          </div>

          {threadsLoading ? (
            <p className="gchat-no-threads">Loading threads...</p>
          ) : threads.length === 0 ? (
            <p className="gchat-no-threads">
              No conversation data yet. Run an analysis job to see results.
            </p>
          ) : (
            <>
              {viewMode === "all" && selectedThreadIds.length > 0 && (
                <div className="gchat-bulk-actions">
                  <span className="gchat-bulk-count">
                    {selectedThreadIds.length} selected
                  </span>
                  <button
                    className="gchat-delete-btn"
                    onClick={handleBulkDelete}
                  >
                    Delete Selected
                  </button>
                </div>
              )}
              <div
                className="ag-theme-quartz gchat-grid-wrapper"
                style={{
                  "--ag-header-background-color": "rgb(61, 39, 255)",
                  "--ag-header-foreground-color": "#fff",
                }}
              >
                <AgGridReact
                  rowData={threads}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  rowSelection={viewMode === "all" ? "multiple" : undefined}
                  onSelectionChanged={
                    viewMode === "all" ? onSelectionChanged : undefined
                  }
                  pagination={true}
                  paginationPageSize={15}
                  animateRows={true}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GChatConversations;
